import React, { useState, useEffect } from 'react';
import {
  Plus,
  RefreshCw,
  Search,
  Filter,
  Users,
  Settings as SettingsIcon,
  Link as LinkIcon,
  X,
  Edit,
  LogOut,
  Crown,
  Volume2,
  Lock,
  Clock
} from 'lucide-react';
import { useGroups } from '../contexts/GroupsContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageActions } from '../contexts/PageActionsContext';
import { CreateGroupFormData, WhatsAppGroup, EPHEMERAL_OPTIONS } from '../types/groupTypes';
import { GroupCard } from '../components/grupos/GroupCard';
import { GroupForm } from '../components/grupos/GroupForm';
import { ParticipantList } from '../components/grupos/ParticipantList';
import { InviteManager } from '../components/grupos/InviteManager';
import { AdminsList } from '../components/grupos/AdminsList';
import toast from 'react-hot-toast';

// ============================================================================
// INTERFACES
// ============================================================================

type ModalType = 'create' | 'details' | 'participants' | 'settings' | 'invite' | null;

interface WhatsAppInstance {
  name: string;
  displayName?: string;
  status: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const GruposWhatsApp: React.FC = () => {
  const {
    groups,
    selectedGroup,
    loading,
    loadGroups,
    selectGroup,
    createGroup,
    updateGroup,
    updateSettings,
    leaveGroup,
    syncGroup,
    addParticipants,
    removeParticipants,
    promoteParticipants,
    demoteParticipants
  } = useGroups();

  const { theme } = useTheme();
  const { isUrlLogin } = useAuth();
  const { registerActions, clearActions, registerMetadata } = usePageActions();

  const [modalType, setModalType] = useState<ModalType>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'participants' | 'admins' | 'invite' | 'settings'>('details');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInstance, setSelectedInstance] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'participants'>('date');
  const [availableInstances, setAvailableInstances] = useState<WhatsAppInstance[]>([]);

  // Carregar instâncias conectadas
  useEffect(() => {
    const loadInstances = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch('/api/waha/sessions', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const sessions = await response.json();
          // Filtrar apenas sessões conectadas
          const connected = sessions
            .filter((s: any) => s.status === 'WORKING' || s.status === 'CONNECTED')
            .map((s: any) => ({
              name: s.name,
              displayName: s.displayName || s.name,
              status: s.status
            }));
          setAvailableInstances(connected);
        }
      } catch (error) {
        console.error('Erro ao carregar instâncias:', error);
      }
    };

    loadInstances();
    loadGroups();
  }, []);

  // Filtrar grupos
  const filteredGroups = groups
    .filter(g => {
      // Filtro por instância
      if (selectedInstance !== 'all' && g.instanceName !== selectedInstance) {
        return false;
      }
      
      // Filtro por busca
      if (searchTerm && !g.subject.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.subject.localeCompare(b.subject);
        case 'participants':
          return b.participantsCount - a.participantsCount;
        case 'date':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

  // Handlers
  const handleCreateGroup = async (data: CreateGroupFormData) => {
    try {
      await createGroup({
        instanceName: data.instanceName,
        subject: data.subject,
        participants: data.participants,
        description: data.description,
        isAnnouncement: data.isAnnouncement,
        ephemeralDuration: data.ephemeralDuration
      });
      setModalType(null);
    } catch (error) {
      console.error('Erro ao criar grupo:', error);
    }
  };

  const handleSelectGroup = async (groupJid: string) => {
    await selectGroup(groupJid);
    setActiveTab('details'); // Reset para aba de detalhes
    setModalType('details');
  };

  const handleSyncGroup = async (groupJid: string) => {
    try {
      await syncGroup(groupJid);
    } catch (error) {
      console.error('Erro ao sincronizar:', error);
    }
  };

  const handleLeaveGroup = async () => {
    if (!selectedGroup) return;

    if (window.confirm(`Tem certeza que deseja sair do grupo "${selectedGroup.subject}"?`)) {
      try {
        await leaveGroup(selectedGroup.groupJid);
        setModalType(null);
      } catch (error) {
        console.error('Erro ao sair do grupo:', error);
      }
    }
  };

  const handleRefresh = async () => {
    const toastId = toast.loading('Ressincronizando grupos... Isso pode levar alguns minutos.', {
      duration: Infinity
    });

    try {
      await loadGroups(selectedInstance === 'all' ? undefined : selectedInstance, true);
      toast.success('✅ Grupos atualizados com sucesso! Nomes, telefones e admins sincronizados.', {
        id: toastId,
        duration: 5000
      });
    } catch (error) {
      toast.error('❌ Erro ao atualizar grupos', {
        id: toastId
      });
    }
  };

  // 🎯 Actions for iframe mode navigation bar
  const gruposActions = (
    <div className="flex items-center gap-3">
      <button
        onClick={handleRefresh}
        disabled={loading}
        className={`px-4 py-2 border rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium ${
          theme === 'dark' 
            ? 'border-gray-600 hover:bg-gray-700'
            : 'border-gray-300 hover:bg-gray-50'
        }`}
        style={{ color: 'var(--text-primary)' }}
      >
        <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        Atualizar
      </button>

      <button
        onClick={() => setModalType('create')}
        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
      >
        <Plus className="w-5 h-5" />
        Criar Grupo
      </button>
    </div>
  );

  // 🎯 Register actions and metadata for iframe mode
  useEffect(() => {
    if (isUrlLogin) {
      registerActions('/grupos', gruposActions);
      registerMetadata('/grupos', {
        title: 'Grupos do WhatsApp',
        subtitle: 'Gerencie seus grupos e participantes de forma centralizada'
      });
    }
    return () => clearActions('/grupos');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUrlLogin, loading, modalType, theme]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Header - Only show in normal mode */}
      {!isUrlLogin && (
      <div className="border-b" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Grupos do WhatsApp</h1>
              <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                Gerencie seus grupos e participantes de forma centralizada
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className={`px-4 py-2 border rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium ${
                  theme === 'dark' 
                    ? 'border-gray-600 hover:bg-gray-700'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
                style={{ color: 'var(--text-primary)' }}
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>

              <button
                onClick={() => setModalType('create')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <Plus className="w-5 h-5" />
                Criar Grupo
              </button>
            </div>
          </div>

          {/* Filtros */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {/* Busca */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Buscar grupos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal"
                style={{
                  backgroundColor: 'var(--bg-card)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>

            {/* Filtro por instância */}
            <select
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal"
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)'
              }}
            >
              <option value="all">Todas as instâncias</option>
              {availableInstances.map(instance => (
                <option key={instance.name} value={instance.name}>
                  {instance.displayName || instance.name}
                </option>
              ))}
            </select>

            {/* Ordenação */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'date' | 'participants')}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal"
              style={{
                backgroundColor: 'var(--bg-card)',
                borderColor: 'var(--border-color)',
                color: 'var(--text-primary)'
              }}
            >
              <option value="date">Mais recentes</option>
              <option value="name">Nome (A-Z)</option>
              <option value="participants">Mais membros</option>
            </select>
          </div>
        </div>
      </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div 
            className="rounded-lg shadow p-6"
            style={{ backgroundColor: 'var(--bg-card)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Total de Grupos</p>
                <p className="text-3xl font-bold mt-2" style={{ color: 'var(--text-primary)' }}>{filteredGroups.length}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                theme === 'dark' ? 'bg-blue-900' : 'bg-blue-100'
              }`}>
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div 
            className="rounded-lg shadow p-6"
            style={{ backgroundColor: 'var(--bg-card)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Total de Membros</p>
                <p className="text-3xl font-bold mt-2" style={{ color: 'var(--text-primary)' }}>
                  {filteredGroups.reduce((acc, g) => acc + g.participantsCount, 0)}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                theme === 'dark' ? 'bg-green-900' : 'bg-green-100'
              }`}>
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div 
            className="rounded-lg shadow p-6"
            style={{ backgroundColor: 'var(--bg-card)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Grupos de Anúncio</p>
                <p className="text-3xl font-bold mt-2" style={{ color: 'var(--text-primary)' }}>
                  {filteredGroups.filter(g => g.isAnnouncement).length}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                theme === 'dark' ? 'bg-purple-900' : 'bg-purple-100'
              }`}>
                <Volume2 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div 
            className="rounded-lg shadow p-6"
            style={{ backgroundColor: 'var(--bg-card)' }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Com Mensagens Temporárias</p>
                <p className="text-3xl font-bold mt-2" style={{ color: 'var(--text-primary)' }}>
                  {filteredGroups.filter(g => g.ephemeralDuration > 0).length}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                theme === 'dark' ? 'bg-orange-900' : 'bg-orange-100'
              }`}>
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Lista de grupos */}
        {loading && groups.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Carregando grupos...</p>
            </div>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              {searchTerm ? 'Nenhum grupo encontrado' : 'Nenhum grupo ainda'}
            </h3>
            <p className="mb-6 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
              {searchTerm
                ? 'Tente ajustar os filtros de busca'
                : 'Comece criando seu primeiro grupo do WhatsApp'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setModalType('create')}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto text-sm font-medium"
              >
                <Plus className="w-5 h-5" />
                Criar Primeiro Grupo
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                onClick={() => handleSelectGroup(group.groupJid)}
                onSync={() => handleSyncGroup(group.groupJid)}
                availableInstances={availableInstances}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal: Criar Grupo */}
      {modalType === 'create' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <GroupForm
            onSubmit={handleCreateGroup}
            onCancel={() => setModalType(null)}
            availableInstances={availableInstances}
            loading={loading}
          />
        </div>
      )}

      {/* Modal: Detalhes do Grupo */}
      {modalType === 'details' && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div 
            className="rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            style={{ backgroundColor: 'var(--bg-card)' }}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  {selectedGroup.pictureUrl ? (
                    <img
                      src={selectedGroup.pictureUrl}
                      alt={selectedGroup.subject}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    selectedGroup.subject.charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-n-slate-12">{selectedGroup.subject}</h2>
                  <p className="text-sm font-normal mt-2" style={{ color: 'var(--text-secondary)' }}>
                    {selectedGroup.participantsCount} {selectedGroup.participantsCount === 1 ? 'membro' : 'membros'}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setModalType(null)}
                className={`p-2 rounded-lg transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b px-6" style={{ borderColor: 'var(--border-color)' }}>
              <div className="flex space-x-8">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`py-4 border-b-2 font-normal text-sm transition-colors ${
                    activeTab === 'details'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Detalhes
                </button>
                <button
                  onClick={() => setActiveTab('participants')}
                  className={`py-4 border-b-2 font-normal text-sm transition-colors ${
                    activeTab === 'participants'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Participantes ({selectedGroup.participantsCount})
                </button>
                <button
                  onClick={() => setActiveTab('admins')}
                  className={`py-4 border-b-2 font-normal text-sm transition-colors ${
                    activeTab === 'admins'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Admins ({selectedGroup.participants?.filter(p => p.isAdmin || p.isSuperAdmin).length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('invite')}
                  className={`py-4 border-b-2 font-normal text-sm transition-colors ${
                    activeTab === 'invite'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Convite
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`py-4 border-b-2 font-normal text-sm transition-colors ${
                    activeTab === 'settings'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Configurações
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'details' && (
                <div className="space-y-6">
                  {selectedGroup.description && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Descrição</h3>
                      <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>{selectedGroup.description}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Instância</h3>
                      <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>{selectedGroup.instanceName}</p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Criado em</h3>
                      <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(selectedGroup.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Última Sincronização</h3>
                      <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>
                        {selectedGroup.lastSyncedAt
                          ? new Date(selectedGroup.lastSyncedAt).toLocaleString('pt-BR')
                          : 'Nunca'}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>ID do Grupo</h3>
                      <p className="text-xs font-normal font-mono break-all" style={{ color: 'var(--text-secondary)' }}>
                        {selectedGroup.groupJid}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => syncGroup(selectedGroup.groupJid)}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
                    >
                      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                      Sincronizar
                    </button>

                    <button
                      onClick={handleLeaveGroup}
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 text-sm font-medium"
                    >
                      <LogOut className="w-4 h-4" />
                      Sair do Grupo
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'participants' && selectedGroup.participants && (
                <ParticipantList
                  participants={selectedGroup.participants}
                  onAdd={() => toast.info('Funcionalidade em desenvolvimento')}
                  onRemove={(jids) => removeParticipants(selectedGroup.groupJid, jids)}
                  onPromote={(jids) => promoteParticipants(selectedGroup.groupJid, jids)}
                  onDemote={(jids) => demoteParticipants(selectedGroup.groupJid, jids)}
                  canManage={true}
                />
              )}

              {activeTab === 'admins' && selectedGroup.participants && (
                <AdminsList
                  participants={selectedGroup.participants}
                />
              )}

              {activeTab === 'invite' && (
                <InviteManager
                  groupJid={selectedGroup.groupJid}
                  initialInviteCode={selectedGroup.inviteCode}
                />
              )}

              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <div 
                    className={`border rounded-lg p-4 ${
                      theme === 'dark'
                        ? 'bg-yellow-900 border-yellow-700'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
                  >
                    <p className={`text-sm font-normal ${
                      theme === 'dark' ? 'text-yellow-200' : 'text-yellow-800'
                    }`}>
                      ⚠️ As alterações nas configurações serão aplicadas imediatamente no grupo do WhatsApp.
                    </p>
                  </div>

                  {/* Configurações atuais */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
                      <div>
                        <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>Modo Anúncio</p>
                        <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Apenas admins podem enviar mensagens</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedGroup.isAnnouncement}
                          onChange={(e) => updateSettings(selectedGroup.groupJid, { isAnnouncement: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${
                          theme === 'dark' ? 'bg-gray-700 after:border-gray-600' : 'bg-gray-200 after:border-gray-300'
                        }`}></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
                      <div>
                        <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>Grupo Travado</p>
                        <p className="text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>Apenas admins editam informações</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedGroup.isLocked}
                          onChange={(e) => updateSettings(selectedGroup.groupJid, { isLocked: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${
                          theme === 'dark' ? 'bg-gray-700 after:border-gray-600' : 'bg-gray-200 after:border-gray-300'
                        }`}></div>
                      </label>
                    </div>

                    <div className="py-3">
                      <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                        Mensagens Temporárias
                      </label>
                      <select
                        value={selectedGroup.ephemeralDuration}
                        onChange={(e) => updateSettings(selectedGroup.groupJid, { ephemeralDuration: Number(e.target.value) })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-normal"
                        style={{
                          backgroundColor: 'var(--bg-card)',
                          borderColor: 'var(--border-color)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        {EPHEMERAL_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GruposWhatsApp;

